import { ContactEnvelopePageSizeType, DepartmentType, PrefixNameType, WithholdingTaxPageSizeType } from "@/enums/enums";
import { i18n } from "@/i18n";
import { AddressDetailModel } from "@/model/address/addressModel";
import { DepartmentModel, OrdinaryPersonModel } from "@/model/contact/contactModel";
import {
  ContactEnvelopeDocumentTemplateModel,
  ContactEnvelopeOnlineViewModel,
  ContactPersonEnvelopeModel,
  ElementContactEnvelopeModel,
  ElementMerchantEnvelopeModel,
} from "@/model/document/contactEnvelopeModel";
import { SettingContactEnvelopeDocumentTemplateModel } from "@/model/document/settingDocumentTemplate";
import { ConvertToAddressDetail } from "./addressHelper";
import { GetDocumentOrdinaryPerson, GetDocumentPersonList } from "./documentFacadeHelper";
import { GetMerchantLogo } from "./documentHelper";

function GetValueBySettingOrDefault(settingValue: any, defaultValue: any) {
  if (settingValue) {
    return settingValue;
  } else if (defaultValue) {
    return defaultValue;
  } else {
    return "-";
  }
}

export function GetContactEnvelopeDocumentTemplate(
  documentModel: ContactEnvelopeOnlineViewModel,
  settingDocumentTemplateDefault?: SettingContactEnvelopeDocumentTemplateModel
): ContactEnvelopeDocumentTemplateModel {
  const documentTemplate: ContactEnvelopeDocumentTemplateModel = {
    transactionType: documentModel.transactionType,
    header: {
      pageSize: documentModel.template.pageSize,
      merchantLogo: GetMerchantLogo(documentModel, settingDocumentTemplateDefault),
      merchant: GetMerchant(documentModel, settingDocumentTemplateDefault),
    },
    contact: GetContact(documentModel, settingDocumentTemplateDefault),
  };

  if (settingDocumentTemplateDefault) {
    documentTemplate.header.pageSize = settingDocumentTemplateDefault.pageSize;
  }

  return documentTemplate;
}
export function GetMerchant(
  documentModel: ContactEnvelopeOnlineViewModel,
  settingDocumentTemplateDefault?: SettingContactEnvelopeDocumentTemplateModel
): ElementMerchantEnvelopeModel {
  const merchant: ElementMerchantEnvelopeModel = {
    merchantName: documentModel.document.data.merchant.name,
    merchantType: documentModel.document.data.merchant.merchantType,
    merchantAddress: ConvertToAddressDetail(documentModel.document.data.merchant.registerAddress),
    merchantAddressTemplate: documentModel.template.merchant.merchantAddressTemplate,
    merchantTaxId: documentModel.document.data.merchant.taxNumber,
    merchantBranchCode: documentModel.document.data.merchant.branchCode,
    phoneNumber: {
      value: documentModel.document.data.merchant.phoneNumber,
      isShowElement: documentModel.template.merchant.phoneNumber.isShowElement,
    },
    email: {
      value: documentModel.document.data.merchant.email,
      isShowElement: documentModel.template.merchant.email.isShowElement,
    },
    website: {
      value: documentModel.document.data.merchant.website,
      isShowElement: documentModel.template.merchant.website?.isShowElement ?? true,
    },
    line: {
      value: documentModel.document.data.merchant.line,
      isShowElement: documentModel.template.merchant.line?.isShowElement ?? true,
    },
    faxNumber: {
      value: documentModel.document.data.merchant.faxNumber,
      isShowElement: documentModel.template.merchant.faxNumber.isShowElement ?? true,
    },
  };

  if (settingDocumentTemplateDefault) {
    merchant.merchantAddressTemplate = settingDocumentTemplateDefault.merchant.merchantAddressTemplate;
    merchant.phoneNumber.isShowElement = settingDocumentTemplateDefault.merchant.phoneNumber.isShowElement;
    merchant.email.isShowElement = settingDocumentTemplateDefault.merchant.email.isShowElement;
    merchant.website.isShowElement = settingDocumentTemplateDefault.merchant.website.isShowElement;
    merchant.line.isShowElement = settingDocumentTemplateDefault.merchant.line.isShowElement;
    merchant.faxNumber.isShowElement = settingDocumentTemplateDefault.merchant.faxNumber.isShowElement;
  }
  return merchant;
}

export function GetContact(
  data: ContactEnvelopeOnlineViewModel,
  settingDocumentTemplateDefault?: SettingContactEnvelopeDocumentTemplateModel
): ElementContactEnvelopeModel {
  const documentContactModel: ElementContactEnvelopeModel = {
    contactId: data.document.data.contact.contactId,
    contactCode: data.document.data.contact.contactCode,
    name: data.document.data.contact.name,
    merchantType: data.document.data.contact.merchantType,
    taxNumber: data.document.data.contact.taxNumber,
    branchCode: data.document.data.contact.branchCode,
    phoneNumber: data.document.data.contact.phoneNumber,
    faxNumber: data.document.data.contact.faxNumber,
    email: data.document.data.contact.email,
    website: data.document.data.contact.website,
    isOrdinaryPerson: data.document.data.contact.isOrdinaryPerson,
    ordinaryPerson: data.document.data.contact.ordinaryPerson
      ? GetDocumentOrdinaryPerson(data.document.data.contact.ordinaryPerson)
      : new OrdinaryPersonModel(),
    persons: data.document.data.contact.persons
      ? GetDocumentPersonList(data.document.data.contact.persons)
      : new Array<ContactPersonEnvelopeModel>(),
    registerAddress: data.document.data.contact.registerAddress
      ? ConvertToAddressDetail(data.document.data.contact.registerAddress)
      : new AddressDetailModel(),
    shippingAddress: data.document.data.contact.shippingAddress
      ? ConvertToAddressDetail(data.document.data.contact.shippingAddress)
      : new AddressDetailModel(),
    isShowContactName: data.template.contact.isShowContactName,
    isShowContactPosition: data.template.contact.isShowContactPosition,
    isShowContactTel: data.template.contact.isShowContactTel,
    personTemplate: data.template.contact.personTemplate ?? data.document.data.contact.persons[0],
  };

  if (settingDocumentTemplateDefault) {
    documentContactModel.isShowContactName = settingDocumentTemplateDefault.contact.isShowContactName;
    documentContactModel.isShowContactPosition = settingDocumentTemplateDefault.contact.isShowContactPosition;
    documentContactModel.isShowContactTel = settingDocumentTemplateDefault.contact.isShowContactTel;
    documentContactModel.personTemplate =
      settingDocumentTemplateDefault.contact.personTemplate ?? data.document.data.contact.persons[0];
  }
  return documentContactModel;
}

export function GetPaperSizeName(pageSize: ContactEnvelopePageSizeType) {
  let paperSizeName: any = "";
  switch (pageSize) {
    case ContactEnvelopePageSizeType.A5:
      paperSizeName = i18n.t("Envelope_Page_Size.A5");
      break;
    case ContactEnvelopePageSizeType.A4:
      paperSizeName = i18n.t("Envelope_Page_Size.A4");
      break;
    case ContactEnvelopePageSizeType.Envelope:
      paperSizeName = i18n.t("Envelope_Page_Size.Envelope");
      break;
    case ContactEnvelopePageSizeType.Standard:
      paperSizeName = i18n.t("Envelope_Page_Size.Standard");
      break;
    case ContactEnvelopePageSizeType.Postcard:
      paperSizeName = i18n.t("Envelope_Page_Size.Postcard");
      break;
  }
  return paperSizeName;
}

export function GetContactPersonName(person: any) {
  let prefixName: any = "";
  switch (person.namePrefixType) {
    case PrefixNameType.None:
      prefixName = "";
      break;
    case PrefixNameType.Khun:
      prefixName = i18n.t("PrefixName.Khun");
      break;
    case PrefixNameType.Mr:
      prefixName = i18n.t("PrefixName.Mr");
      break;
    case PrefixNameType.Mrs:
      prefixName = i18n.t("PrefixName.Mrs");
      break;
    case PrefixNameType.Ms:
      prefixName = i18n.t("PrefixName.Ms");
      break;
    case PrefixNameType.Other:
      prefixName = person.namePrefix;
      break;
  }
  return prefixName + person.firstname + " " + person.lastname;
}

export function GetDepartmentName(department: DepartmentModel) {
  let departmentName: any = "";
  switch (department.departmentType) {
    case DepartmentType.None:
      departmentName = "";
      break;
    case DepartmentType.GeneralManagement:
      departmentName = i18n.t("DepartmentType.GeneralManagement");
      break;
    case DepartmentType.GeneralAdministration:
      departmentName = i18n.t("DepartmentType.GeneralAdministration");
      break;
    case DepartmentType.Sales:
      departmentName = i18n.t("DepartmentType.Sales");
      break;
    case DepartmentType.Marketing:
      departmentName = i18n.t("DepartmentType.Marketing");
      break;
    case DepartmentType.Accounting:
      departmentName = i18n.t("DepartmentType.Accounting");
      break;
    case DepartmentType.Finance:
      departmentName = i18n.t("DepartmentType.Finance");
      break;
    case DepartmentType.HumanResource:
      departmentName = i18n.t("DepartmentType.HumanResource");
      break;
    case DepartmentType.Production:
      departmentName = i18n.t("DepartmentType.Production");
      break;
    case DepartmentType.Operation:
      departmentName = i18n.t("DepartmentType.Operation");
      break;
    case DepartmentType.InformationTechnology:
      departmentName = i18n.t("DepartmentType.InformationTechnology");
      break;
    case DepartmentType.Purchasing:
      departmentName = i18n.t("DepartmentType.Purchasing");
      break;
    case DepartmentType.ResearchAndDevelopment:
      departmentName = i18n.t("DepartmentType.ResearchAndDevelopment");
      break;
    case DepartmentType.Other:
      departmentName = department.departmentOther;
      break;
  }
  return departmentName;
}
export function GetPaperSizeClassName(pageSize: ContactEnvelopePageSizeType) {
  let paperSizeClass = "";
  switch (pageSize) {
    case ContactEnvelopePageSizeType.A5:
      paperSizeClass = "paperA5Landscape";
      break;
    case ContactEnvelopePageSizeType.A4:
      paperSizeClass = "paperA4Landscape";
      break;
    case ContactEnvelopePageSizeType.Envelope:
      paperSizeClass = "paperEnvelope";
      break;
    case ContactEnvelopePageSizeType.Standard:
      paperSizeClass = "paperStandard";
      break;
    case ContactEnvelopePageSizeType.Postcard:
      paperSizeClass = "paperPostcard";
      break;
  }
  return paperSizeClass;
}

export function GetWHTPaperSizeClassName(pageSize: WithholdingTaxPageSizeType) {
  let paperSizeClass = "";
  switch (pageSize) {
    case WithholdingTaxPageSizeType.A5:
      paperSizeClass = "paperA5Landscape";
      break;
    case WithholdingTaxPageSizeType.A4:
      paperSizeClass = "paperA4";
      break;
  }
  return paperSizeClass;
}
